/**
 * AI Conversation API Service
 *
 * Handles all API calls for AI chat conversation persistence.
 * Enables saving, loading, and managing chat conversations.
 */

import { api } from './client';

/**
 * Message role enum matching backend
 */
export enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

/**
 * Conversation status enum matching backend
 */
export enum ConversationStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  FAILED = 'failed',
  ARCHIVED = 'archived',
}

/**
 * AI Message interface for conversation messages
 */
export interface AIMessage {
  id: string;
  role: MessageRole;
  content: string;
  timestamp: Date | string;
  tokens?: number;
  metadata?: Record<string, any>;
  cached?: boolean;
  cacheHit?: boolean;
}

/**
 * Conversation summary for list view (without full messages)
 */
export interface ConversationSummary {
  _id: string;
  title: string;
  status: ConversationStatus;
  provider: string;
  aiModel: string;
  totalTokens: number;
  totalCost: number;
  messageCount: number;
  lastMessage: AIMessage | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Full conversation with messages
 */
export interface Conversation {
  _id: string;
  userId: string;
  projectId?: string;
  title: string;
  messages: AIMessage[];
  context: {
    systemPrompt?: string;
    componentData?: any[];
    projectData?: any;
    userPreferences?: any;
    conversationHistory?: AIMessage[];
  };
  provider: string;
  aiModel: string;
  metadata: {
    provider: string;
    aiModel: string;
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    frequencyPenalty?: number;
    presencePenalty?: number;
    responseTime: number;
    retryCount: number;
    cacheHits: number;
  };
  status: ConversationStatus;
  totalTokens: number;
  totalCost: number;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request types for API calls
 */
export interface CreateConversationRequest {
  title?: string;
  projectId?: string;
  initialMessage?: string;
  provider?: string;
  aiModel?: string;
}

export interface UpdateConversationRequest {
  title?: string;
  status?: ConversationStatus;
}

export interface AddMessageRequest {
  content: string;
  role: MessageRole;
  metadata?: Record<string, any>;
}

export interface AddMessagesBatchRequest {
  messages: Array<{
    content: string;
    role: MessageRole;
    id?: string;
    timestamp?: string | Date;
    metadata?: Record<string, any>;
  }>;
}

/**
 * Response types for API calls
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ListConversationsResponse {
  success: boolean;
  data: ConversationSummary[];
  meta: {
    pagination: PaginationMeta;
    timestamp: string;
  };
}

export interface GetConversationResponse {
  success: boolean;
  data: Conversation;
  meta: {
    messageCount: number;
    timestamp: string;
  };
}

export interface CreateConversationResponse {
  success: boolean;
  data: Conversation;
  meta: {
    timestamp: string;
  };
}

export interface UpdateConversationResponse {
  success: boolean;
  data: Conversation;
  meta: {
    timestamp: string;
  };
}

export interface DeleteConversationResponse {
  success: boolean;
  data: {
    deleted: boolean;
    conversationId: string;
  };
  meta: {
    timestamp: string;
  };
}

export interface AddMessageResponse {
  success: boolean;
  data: {
    message: AIMessage;
    conversationId: string;
    messageCount: number;
  };
  meta: {
    timestamp: string;
  };
}

export interface AddMessagesBatchResponse {
  success: boolean;
  data: {
    addedCount: number;
    conversationId: string;
    totalMessageCount: number;
  };
  meta: {
    timestamp: string;
  };
}

export interface GetActiveConversationResponse {
  success: boolean;
  data: Conversation | null;
  meta: {
    found: boolean;
    timestamp: string;
  };
}

/**
 * Conversation API Service
 */
export const conversationApi = {
  /**
   * List user's conversations with pagination
   */
  async listConversations(params?: {
    page?: number;
    limit?: number;
    projectId?: string;
    status?: ConversationStatus;
  }): Promise<ListConversationsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.projectId) queryParams.set('projectId', params.projectId);
    if (params?.status) queryParams.set('status', params.status);

    const queryString = queryParams.toString();
    const url = `/ai/conversations${queryString ? `?${queryString}` : ''}`;

    return api.get<ListConversationsResponse['data']>(url) as Promise<ListConversationsResponse>;
  },

  /**
   * Get a specific conversation with all messages
   */
  async getConversation(conversationId: string): Promise<GetConversationResponse> {
    return api.get<Conversation>(`/ai/conversations/${conversationId}`) as Promise<GetConversationResponse>;
  },

  /**
   * Create a new conversation
   */
  async createConversation(data: CreateConversationRequest = {}): Promise<CreateConversationResponse> {
    return api.post<Conversation>('/ai/conversations', data) as Promise<CreateConversationResponse>;
  },

  /**
   * Update conversation properties (title, status)
   */
  async updateConversation(
    conversationId: string,
    data: UpdateConversationRequest
  ): Promise<UpdateConversationResponse> {
    return api.patch<Conversation>(`/ai/conversations/${conversationId}`, data) as Promise<UpdateConversationResponse>;
  },

  /**
   * Delete a conversation (soft delete)
   */
  async deleteConversation(conversationId: string): Promise<DeleteConversationResponse> {
    return api.delete<DeleteConversationResponse['data']>(
      `/ai/conversations/${conversationId}`
    ) as Promise<DeleteConversationResponse>;
  },

  /**
   * Add a single message to a conversation
   */
  async addMessage(conversationId: string, data: AddMessageRequest): Promise<AddMessageResponse> {
    return api.post<AddMessageResponse['data']>(
      `/ai/conversations/${conversationId}/messages`,
      data
    ) as Promise<AddMessageResponse>;
  },

  /**
   * Add multiple messages to a conversation (batch sync)
   */
  async addMessagesBatch(
    conversationId: string,
    data: AddMessagesBatchRequest
  ): Promise<AddMessagesBatchResponse> {
    return api.post<AddMessagesBatchResponse['data']>(
      `/ai/conversations/${conversationId}/messages/batch`,
      data
    ) as Promise<AddMessagesBatchResponse>;
  },

  /**
   * Get the most recent active conversation
   */
  async getActiveConversation(projectId?: string): Promise<GetActiveConversationResponse> {
    const url = projectId
      ? `/ai/conversations/active?projectId=${projectId}`
      : '/ai/conversations/active';

    return api.get<Conversation | null>(url) as Promise<GetActiveConversationResponse>;
  },
};

export default conversationApi;
